import React from "react";
import { styled } from "@linaria/react";
import useEnsuredForwardedRef from "../lib/hooks/useEnsuredForwardedRef";
import usePrevious from "../lib/hooks/usePrevious";
import {
  KEYBOARD_ACTIVABLE_CN,
  KEYBOARD_ACTIVE_CN,
  CHECKBOX_CN,
  SELECTED_CN,
  CLOSE_ON_CLICK_CN,
  DISABLED_CN,
} from "../Option";

interface P {
  onRequestClose: () => void;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  opened?: boolean;
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
`;

const Wrapper = styled.div`
  /* overflow-y: auto; */
`;

const OptionList = (
  { onRequestClose, children, className, style, opened }: P,
  forwardedRef: React.Ref<HTMLDivElement>,
) => {
  const wrapperRef = useEnsuredForwardedRef(forwardedRef);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [kbActiveIndex, setKbActiveIndex] = React.useState<number>();
  const wasFocused = React.useRef<HTMLElement | null>();
  const oldOpened = usePrevious(opened);

  React.useLayoutEffect(() => {
    if (opened === false) setKbActiveIndex(undefined);
  }, [setKbActiveIndex, opened]);

  React.useLayoutEffect(() => {
    if (opened === undefined || (opened === true && oldOpened === false)) {
      wasFocused.current = window.document.activeElement as HTMLElement | null;
    }
  }, [opened, oldOpened]);

  React.useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) throw new Error("Missing wrapper");
    const content = contentRef.current;
    if (!content) throw new Error("Missing contentRef");
    const wasFocusedElement = wasFocused.current;

    const autofocusable = content.querySelector("[data-autofocus]") as HTMLElement | undefined;

    // opening
    if (opened === undefined || (opened === true && oldOpened === false))
      window.requestAnimationFrame(() => {
        if (autofocusable) content.setAttribute("tabindex", "-1");
        (autofocusable || content).focus({ preventScroll: true });
      });

    // closing with opened flag
    if (opened === false && oldOpened === true) {
      if (
        wrapper.contains(document.activeElement) &&
        wasFocusedElement &&
        "focus" in wasFocusedElement
      )
        window.requestAnimationFrame(() => {
          wasFocusedElement.focus({ preventScroll: true });
        });
    }

    // closing without opened flag
    if (opened === undefined)
      return () => {
        if (
          wrapper.contains(document.activeElement) &&
          wasFocusedElement &&
          "focus" in wasFocusedElement
        )
          window.requestAnimationFrame(() => {
            wasFocusedElement.focus({ preventScroll: true });
          });
      };
    return;
  }, [wrapperRef, opened, oldOpened]);

  React.useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) throw new Error("Missing wrapper");
    const content = contentRef.current;
    if (!content) throw new Error("Missing content");
    const kbActivables = [...wrapper.getElementsByClassName(KEYBOARD_ACTIVABLE_CN)];
    window.requestAnimationFrame(() => {
      if (typeof kbActiveIndex !== "number") return;
      const kbActivable = kbActivables[kbActiveIndex];
      if (kbActivable) {
        kbActivable.classList.add(KEYBOARD_ACTIVE_CN);
        const closestFocusedActivable = document.activeElement?.closest(
          `.${KEYBOARD_ACTIVABLE_CN}`,
        );
        // move focus to kbActivable if focus is on an element of the list
        // to avoid "clicking it" with spacebar user-agent click
        if (
          closestFocusedActivable &&
          content.contains(closestFocusedActivable) &&
          kbActivable instanceof HTMLElement
        ) {
          kbActivable.focus();
        }
      } else {
        setKbActiveIndex(undefined);
      }
    });

    return () => {
      window.requestAnimationFrame(() => {
        if (typeof kbActiveIndex === "number")
          kbActivables[kbActiveIndex]?.classList.remove(KEYBOARD_ACTIVE_CN);
      });
    };
  }, [children, kbActiveIndex, wrapperRef]);

  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      const target = e.target as HTMLElement;
      const targetCloseOnClick = target.closest(`.${CLOSE_ON_CLICK_CN}`);

      if (targetCloseOnClick && !targetCloseOnClick?.classList.contains(DISABLED_CN))
        onRequestClose();
    },
    [onRequestClose],
  );

  const handleKeydown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const wrapperElement = wrapperRef.current;
      if (!wrapperElement) throw new Error("wrapperElement missing");

      const kbActivables = [
        ...wrapperElement.getElementsByClassName(KEYBOARD_ACTIVABLE_CN),
      ] as HTMLElement[];
      const selectedElement = kbActivables.find(
        e => e.classList.contains(SELECTED_CN) && !e.classList.contains(CHECKBOX_CN),
      );
      const selectedIndex = selectedElement ? kbActivables.indexOf(selectedElement) : -1;
      const kbActiveElement =
        kbActivables.find(e => e.classList.contains(KEYBOARD_ACTIVE_CN)) || selectedElement;

      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onRequestClose();
      }

      if (["ArrowDown", "ArrowUp"].includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();
        setKbActiveIndex(activeIndex => {
          const index = activeIndex ?? selectedIndex;
          const lastIndex = kbActivables.length - 1;
          const up = e.key === "ArrowUp";
          return up ? (index > 0 ? index - 1 : lastIndex) : index === lastIndex ? 0 : index + 1;
        });
      }

      if (e.key === "Enter" && kbActiveElement) {
        e.preventDefault();
        e.stopPropagation();
        kbActiveElement.click();
      }

      if (e.key === " " && kbActiveElement) {
        e.preventDefault();
        e.stopPropagation();
        // do not click kbActiveElement if it is the focused element: user agent uses an implicit click
        if (document.activeElement !== kbActiveElement) kbActiveElement.click();
      }
    },
    [onRequestClose, wrapperRef],
  );

  return (
    <Wrapper
      className={className}
      style={style}
      ref={wrapperRef}
      onKeyDown={handleKeydown}
      onClick={handleClick}
    >
      <Content ref={contentRef} tabIndex={0}>
        {children}
      </Content>
    </Wrapper>
  );
};

export default React.memo(React.forwardRef(OptionList));
